import { Admin, CustomRoutes, Layout, Resource, type LayoutProps } from "react-admin";
import { Route } from "react-router-dom";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import BoltIcon from "@mui/icons-material/Bolt";
import PeopleIcon from "@mui/icons-material/People";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import HistoryIcon from "@mui/icons-material/History";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaymentsIcon from "@mui/icons-material/Payments";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import LinkIcon from "@mui/icons-material/Link";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import HandshakeIcon from "@mui/icons-material/Handshake";
import SavingsIcon from "@mui/icons-material/Savings";
import RedeemIcon from "@mui/icons-material/Redeem";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";

import { authProvider } from "./authProvider";
import { dataProvider } from "./dataProvider";
import { Dashboard } from "./resources/Dashboard";
import { WalletList } from "./resources/wallets/List";
import { WalletCreate } from "./resources/wallets/Create";
import { WalletEdit } from "./resources/wallets/Edit";
import { TransactionList } from "./resources/transactions/List";
import { RentalList } from "./resources/rentals/List";
import { UserList } from "./resources/users/List";
import { KycList } from "./resources/kyc/List";
import { KycShow } from "./resources/kyc/Show";
import { OrderList } from "./resources/orders/List";
import { OrderShow } from "./resources/orders/Show";
import { WithdrawalList } from "./resources/withdrawals/List";
import { WithdrawalShow } from "./resources/withdrawals/Show";
import { SofList } from "./resources/sof/List";
import { SofShow } from "./resources/sof/Show";
import { SupportList } from "./resources/support/List";
import { SupportShow } from "./resources/support/Show";
import { AuditList } from "./resources/audit/List";
import { SettingsPage } from "./resources/Settings";
import { ReportsPage } from "./resources/Reports";
import { PayerBankList } from "./resources/payer-banks/List";
import { PayerBankCreate } from "./resources/payer-banks/Create";
import { PayerBankEdit } from "./resources/payer-banks/Edit";
import { CardDesignList } from "./resources/card-designs/List";
import { CardDesignCreate } from "./resources/card-designs/Create";
import { CardDesignEdit } from "./resources/card-designs/Edit";
import { CardConversionList } from "./resources/card-conversions/List";
import { PromoList } from "./resources/promo-codes/List";
import { PromoCreate } from "./resources/promo-codes/Create";
import { PromoEdit } from "./resources/promo-codes/Edit";
import { PromoBindingList } from "./resources/promo-bindings/List";
import { PromoBindingEdit } from "./resources/promo-bindings/Edit";
import { PartnerList } from "./resources/partners/List";
import { PartnerShow } from "./resources/partners/Show";
import { AccrualList } from "./resources/accruals/List";
import { PartnerPayoutList } from "./resources/partner-payouts/List";
import { PartnerPayoutShow } from "./resources/partner-payouts/Show";
import { CustomMenu } from "./CustomMenu";
import { i18nProvider } from "./i18n";
import { lightTheme, darkTheme } from "./theme";

const MyLayout = (props: LayoutProps) => <Layout {...props} menu={CustomMenu} />;

export default function App() {
  return (
    <Admin
      title="USDTBuy Admin"
      authProvider={authProvider}
      dataProvider={dataProvider}
      dashboard={Dashboard}
      layout={MyLayout}
      i18nProvider={i18nProvider}
      theme={lightTheme}
      darkTheme={darkTheme}
      requireAuth
    >
      {(permissions) => {
        const isAdmin = permissions === "admin";
        return (
          <>
            <Resource
              name="orders"
              icon={ReceiptLongIcon}
              list={OrderList}
              show={OrderShow}
              options={{ label: "Заявки" }}
            />
            <Resource
              name="kyc-applicants"
              icon={VerifiedUserIcon}
              list={KycList}
              show={KycShow}
              options={{ label: "KYC" }}
            />
            <Resource
              name="sof"
              icon={VerifiedUserIcon}
              list={SofList}
              show={SofShow}
              options={{ label: "ИПДС" }}
            />
            {isAdmin && (
              <>
                <Resource
                  name="withdrawals"
                  icon={PaymentsIcon}
                  list={WithdrawalList}
                  show={WithdrawalShow}
                  options={{ label: "Заявки на вывод" }}
                />
                <Resource
                  name="support-threads"
                  icon={SupportAgentIcon}
                  list={SupportList}
                  show={SupportShow}
                  options={{ label: "Поддержка" }}
                />
                <Resource
                  name="wallets"
                  icon={AccountBalanceWalletIcon}
                  list={WalletList}
                  create={WalletCreate}
                  edit={WalletEdit}
                  options={{ label: "Кошельки" }}
                />
                <Resource
                  name="transactions"
                  icon={SwapHorizIcon}
                  list={TransactionList}
                  options={{ label: "Транзакции" }}
                />
                <Resource
                  name="payer-banks"
                  icon={AccountBalanceIcon}
                  list={PayerBankList}
                  create={PayerBankCreate}
                  edit={PayerBankEdit}
                  options={{ label: "Банки оплаты" }}
                />
                <Resource
                  name="card-designs"
                  icon={CreditCardIcon}
                  list={CardDesignList}
                  create={CardDesignCreate}
                  edit={CardDesignEdit}
                  options={{ label: "Карты" }}
                />
                <Resource
                  name="card-conversions"
                  icon={CurrencyExchangeIcon}
                  list={CardConversionList}
                  options={{ label: "Конвертации" }}
                />
                <Resource
                  name="rentals"
                  icon={BoltIcon}
                  list={RentalList}
                  options={{ label: "Energy rentals" }}
                />
                <Resource
                  name="users"
                  icon={PeopleIcon}
                  list={UserList}
                  options={{ label: "Пользователи" }}
                />
                <Resource
                  name="partners"
                  icon={HandshakeIcon}
                  list={PartnerList}
                  show={PartnerShow}
                  options={{ label: "Партнёры" }}
                />
                <Resource
                  name="accruals"
                  icon={SavingsIcon}
                  list={AccrualList}
                  options={{ label: "Начисления" }}
                />
                <Resource
                  name="partner-payouts"
                  icon={RedeemIcon}
                  list={PartnerPayoutList}
                  show={PartnerPayoutShow}
                  options={{ label: "Выплаты партнёрам" }}
                />
                <Resource
                  name="promo-codes"
                  icon={LocalOfferIcon}
                  list={PromoList}
                  create={PromoCreate}
                  edit={PromoEdit}
                  options={{ label: "Промокоды" }}
                />
                <Resource
                  name="promo-bindings"
                  icon={LinkIcon}
                  list={PromoBindingList}
                  edit={PromoBindingEdit}
                  options={{ label: "Привязки промокодов" }}
                />
                <Resource
                  name="audit"
                  icon={HistoryIcon}
                  list={AuditList}
                  options={{ label: "Audit log" }}
                />
                <CustomRoutes>
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/reports" element={<ReportsPage />} />
                </CustomRoutes>
              </>
            )}
          </>
        );
      }}
    </Admin>
  );
}
